import React, { useState, useContext, useEffect, useRef } from 'react';
import { navigate, RouteComponentProps } from '@reach/router';
import {
  Box,
  Grid,
  LinearProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Link
} from '@mui/material';
import { Alert, Button, TextField } from '../../../atoms';
import StyledLogin from './login.style';
import { app, auth } from '../../../firebase';
import { signInWithEmailAndPassword, sendPasswordResetEmail, signOut } from 'firebase/auth';
import { ApplicationContext, materialTheme } from '@particles';
import { callGetUser } from '@api';
import { ReactComponent as IconSalesrook } from '@assets/svg/salesrook.svg';
import Validator from 'validator';

const GOLD = '#f0cf69';
const BRAND_GRADIENT = 'linear-gradient(135deg, #3182ce 0%, #887bdc 52%, #2b2258 100%)';

const inputSx = {
  position: 'relative',
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    backgroundColor: '#F8F9FB',
    transition: 'background-color 220ms ease',
    '&:hover': {
      backgroundColor: '#F1F3F7'
    },
    '&.Mui-focused': {
      backgroundColor: '#ffffff'
    },
    '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
      borderColor: 'rgba(43, 34, 88, 0.18)',
      borderWidth: 1
    }
  },
  '& .MuiInputLabel-root.Mui-focused': {
    color: '#2b2258'
  },
  '&::after': {
    content: '""',
    position: 'absolute',
    left: 12,
    right: 12,
    bottom: 0,
    height: 2,
    backgroundColor: GOLD,
    transform: 'scaleX(0)',
    transformOrigin: 'left center',
    transition: 'transform 240ms cubic-bezier(0.4, 0, 0.2, 1)',
    pointerEvents: 'none',
    borderRadius: 2
  },
  '&:focus-within::after': {
    transform: 'scaleX(1)'
  }
};

let navigating = false;
const Login: React.FC<RouteComponentProps> = () => {
  const {
    state: { user },
    dispatch
  } = useContext(ApplicationContext);
  const [loading, setLoading] = useState<boolean>(false);
  const userInteracted = useRef<boolean>(false);

  useEffect(() => {
    if (user && !navigating) {
      navigating = true;
      navigate(`/`);
      setLoading(false);
    }
  }, [user, navigating]);

  useEffect(() => {
    app.name;
  }, []);

  const [values, setValues] = useState({
    email: '',
    password: ''
  });

  const [error, setError] = useState<string>();
  const [forgotPasswordOpen, setForgotPasswordOpen] = useState<boolean>(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState<string>('');
  const [forgotPasswordError, setForgotPasswordError] = useState<string>('');
  const [forgotPasswordSuccess, setForgotPasswordSuccess] = useState<string>('');
  const [forgotPasswordLoading, setForgotPasswordLoading] = useState<boolean>(false);

  const [errors, setErrors] = useState({
    email: '',
    password: ''
  });

  const validateFields = () => {
    let error = false;

    setErrors({
      email: '',
      password: ''
    });

    if (!Validator.isEmail(values.email)) {
      error = true;
      setErrors((state) => ({ ...state, email: 'Enter a valid email address' }));
    }

    if (values.password.length < 6) {
      error = true;
      setErrors((state) => ({ ...state, password: 'Enter a minimum of 6 characters' }));
    }

    return error;
  };

  const handleChange = (prop: string) => (event: React.ChangeEvent<HTMLInputElement>) => {
    setValues({ ...values, [prop]: event.target.value });
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (event.key === 'Enter') {
      userInteracted.current = true;
      onLogin();
    }
  };

  const onLogin = () => {
    if (!userInteracted.current) {
      return;
    }

    if (validateFields()) {
      return;
    }

    setLoading(true);
    signInWithEmailAndPassword(auth, values.email, values.password)
      .then(async (userCredential) => {
        const user = userCredential.user;

        const token = await user.getIdToken();
        localStorage.setItem('token', token);

        callGetUser({ email: user.email ?? '' })
          .then((value) => {
            dispatch({ key: 'user', value: value.data, type: 'set' });
            localStorage.setItem('user', JSON.stringify(value.data));
            navigating = false;
          })
          .catch(() => {
            setLoading(false);
            localStorage.clear();
            signOut(auth).catch((err) => console.warn('Firebase sign-out failed:', err));
          });
      })
      .catch((error) => {
        setLoading(false);
        const errorMessage = error.message as string;
        if (errorMessage.includes('auth/wrong-password')) {
          setError('Password provided is incorrect!');
        } else if (errorMessage.includes('auth/user-not-found')) {
          setError('No user found for this email address!');
        } else {
          setError(errorMessage);
        }
        setTimeout(() => setError(undefined), 3000);
      });
  };

  const handleForgotPassword = () => {
    setForgotPasswordError('');
    setForgotPasswordSuccess('');

    if (!Validator.isEmail(forgotPasswordEmail)) {
      setForgotPasswordError('Please enter a valid email address');
      return;
    }

    setForgotPasswordLoading(true);
    sendPasswordResetEmail(auth, forgotPasswordEmail)
      .then(() => {
        setForgotPasswordLoading(false);
        setForgotPasswordSuccess('Password reset email sent! Please check your inbox.');
        setTimeout(() => {
          setForgotPasswordOpen(false);
          setForgotPasswordEmail('');
          setForgotPasswordSuccess('');
        }, 3000);
      })
      .catch((error) => {
        setForgotPasswordLoading(false);
        const errorMessage = error.message as string;
        if (errorMessage.includes('auth/user-not-found')) {
          setForgotPasswordError('No user found for this email address!');
        } else {
          setForgotPasswordError(errorMessage);
        }
      });
  };

  const handleCloseForgotPassword = () => {
    setForgotPasswordOpen(false);
    setForgotPasswordEmail('');
    setForgotPasswordError('');
    setForgotPasswordSuccess('');
  };

  return (
    <StyledLogin>
      <Grid container sx={{ height: '100vh' }}>
        {/* Left — brand panel matching the sidebar gradient */}
        <Grid
          item
          xs={0}
          md={6}
          sx={{
            display: { xs: 'none', md: 'flex' },
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            position: 'relative',
            overflow: 'hidden',
            background: BRAND_GRADIENT,
            /* Subtle gold-tinted geometric mesh */
            '&::before': {
              content: '""',
              position: 'absolute',
              inset: 0,
              backgroundImage: `
                linear-gradient(115deg, rgba(240, 207, 105, 0.06) 1px, transparent 1px),
                linear-gradient(65deg, rgba(240, 207, 105, 0.04) 1px, transparent 1px)
              `,
              backgroundSize: '48px 48px, 72px 72px',
              opacity: 0.9,
              pointerEvents: 'none'
            },
            /* Vertical gold accent bar on inner edge */
            '&::after': {
              content: '""',
              position: 'absolute',
              top: 0,
              right: 0,
              bottom: 0,
              width: '3px',
              background: `linear-gradient(180deg, transparent 0%, ${GOLD} 30%, ${GOLD} 70%, transparent 100%)`,
              opacity: 0.55,
              pointerEvents: 'none'
            }
          }}
        >
          <Box
            sx={{
              position: 'relative',
              zIndex: 1,
              display: 'flex',
              flexDirection: 'column',
              alignItems: 'center',
              textAlign: 'center',
              px: 4,
              animation: 'brandFadeIn 500ms ease-out both'
            }}
          >
            {/* Logo with 'being written' reveal — clip-path wipe left→right
                with a gold pen tip trailing the reveal edge */}
            <Box
              sx={{
                position: 'relative',
                display: 'inline-block',
                mb: 4,
                width: '100%',
                maxWidth: '380px'
              }}
            >
              <Box
                sx={{
                  animation: 'logoWrite 1800ms cubic-bezier(0.65, 0, 0.35, 1) 500ms 1 both'
                }}
              >
                <IconSalesrook
                  style={{
                    width: '100%',
                    height: 'auto',
                    display: 'block'
                  }}
                />
              </Box>
              {/* Pen tip — a gold vertical bar with soft trail that
                  translates left→right in sync with the reveal */}
              <Box
                aria-hidden
                sx={{
                  position: 'absolute',
                  top: 0,
                  bottom: 0,
                  left: '0%',
                  width: '3px',
                  background: `linear-gradient(90deg, rgba(240, 207, 105, 0) 0%, rgba(240, 207, 105, 0.35) 30%, ${GOLD} 100%)`,
                  boxShadow: `0 0 12px ${GOLD}, 0 0 24px rgba(240, 207, 105, 0.6)`,
                  borderRadius: 2,
                  transform: 'translateX(-1.5px)',
                  animation: 'penMove 1800ms cubic-bezier(0.65, 0, 0.35, 1) 500ms 1 both',
                  pointerEvents: 'none'
                }}
              >
                {/* Pen 'dot' — small bright pulse at the tip for extra polish */}
                <Box
                  sx={{
                    position: 'absolute',
                    top: '50%',
                    left: '50%',
                    width: 8,
                    height: 8,
                    borderRadius: '50%',
                    backgroundColor: GOLD,
                    boxShadow: `0 0 8px ${GOLD}, 0 0 16px rgba(240, 207, 105, 0.9)`,
                    animation: 'penDot 900ms ease-in-out infinite'
                  }}
                />
              </Box>
            </Box>

            <Box
              sx={{
                display: 'inline-block',
                position: 'relative',
                mb: 2,
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  bottom: -10,
                  width: 48,
                  height: 3,
                  backgroundColor: GOLD,
                  borderRadius: 2,
                  transform: 'translateX(-50%)'
                }
              }}
            >
              <Typography
                variant="h3"
                sx={{
                  color: 'white',
                  fontWeight: 700,
                  fontSize: { md: '2.4rem', lg: '2.75rem' },
                  fontFamily: materialTheme.typography.fontFamily,
                  letterSpacing: '-0.5px'
                }}
              >
                Welcome Back
              </Typography>
            </Box>

            <Typography
              variant="body1"
              sx={{
                mt: 4,
                color: 'rgba(255, 255, 255, 0.82)',
                fontSize: '1.05rem',
                maxWidth: '460px',
                mx: 'auto',
                lineHeight: 1.6,
                fontFamily: materialTheme.typography.fontFamily
              }}
            >
              Access your enterprise dashboard and manage your business operations with confidence
              and security.
            </Typography>
          </Box>
        </Grid>

        {/* Right — sign-in card */}
        <Grid
          item
          xs={12}
          md={6}
          sx={{
            display: 'flex',
            flexDirection: 'column',
            justifyContent: 'center',
            alignItems: 'center',
            backgroundColor: '#FBFBFD',
            position: 'relative',
            px: { xs: 3, sm: 4, md: 6 }
          }}
        >
          {loading && (
            <LinearProgress
              sx={{
                position: 'absolute',
                top: 0,
                left: 0,
                right: 0,
                height: '3px',
                zIndex: 10,
                '& .MuiLinearProgress-bar': {
                  backgroundColor: GOLD
                },
                backgroundColor: 'rgba(240, 207, 105, 0.18)'
              }}
            />
          )}

          <Box
            sx={{
              width: '100%',
              maxWidth: '460px',
              backgroundColor: 'white',
              borderRadius: '16px',
              boxShadow:
                '0 1px 2px rgba(43, 34, 88, 0.04), 0 8px 24px rgba(43, 34, 88, 0.06), 0 24px 60px rgba(43, 34, 88, 0.05)',
              p: { xs: 4, sm: 5, md: 6 },
              position: 'relative',
              animation: 'cardEnter 420ms ease-out both'
            }}
          >
            {/* Logo (mobile only) */}
            <Box
              sx={{
                display: { xs: 'block', md: 'none' },
                mb: 4,
                textAlign: 'center'
              }}
            >
              <IconSalesrook
                style={{
                  width: '200px',
                  height: 'auto',
                  margin: '0 auto'
                }}
              />
            </Box>

            <Typography
              variant="h4"
              sx={{
                fontWeight: 700,
                color: materialTheme.palette.text.primary,
                mb: 1,
                fontSize: { xs: '1.75rem', sm: '2rem' },
                fontFamily: materialTheme.typography.fontFamily,
                letterSpacing: '-0.4px'
              }}
            >
              Sign In
            </Typography>
            <Typography
              variant="body2"
              sx={{
                color: materialTheme.palette.text.secondary,
                mb: 4,
                fontSize: '0.95rem',
                fontFamily: materialTheme.typography.fontFamily
              }}
            >
              Enter your credentials to access your account
            </Typography>

            <Box sx={{ mb: 3 }}>
              <TextField
                name="email"
                label="Email Address"
                fullWidth
                required
                type={'email'}
                onChange={handleChange('email')}
                placeholder={'name@company.com'}
                errorMessage={errors.email}
                value={values.email}
                sx={inputSx}
              />
            </Box>

            <Box sx={{ mb: 2 }}>
              <TextField
                name="password"
                label="Password"
                fullWidth
                required
                type="password"
                onChange={handleChange('password')}
                onKeyDown={handleKeyDown}
                placeholder={'Enter your password'}
                errorMessage={errors.password}
                value={values.password}
                sx={inputSx}
              />
            </Box>

            <Box sx={{ mb: 3, textAlign: 'right' }}>
              <Link
                component="button"
                variant="body2"
                onClick={() => setForgotPasswordOpen(true)}
                sx={{
                  color: '#2b2258',
                  textDecoration: 'none',
                  fontFamily: materialTheme.typography.fontFamily,
                  fontSize: '0.875rem',
                  fontWeight: 600,
                  transition: 'color 200ms ease',
                  '&:hover': {
                    textDecoration: 'none',
                    color: GOLD
                  }
                }}
              >
                Forgot Password?
              </Link>
            </Box>

            {error && (
              <Box sx={{ mb: 3 }}>
                <Alert severity={'error'} title={'Error'} message={error} />
              </Box>
            )}

            <Button
              onClick={() => {
                userInteracted.current = true;
                onLogin();
              }}
              fullWidth
              variant="contained"
              sx={{
                mt: 2,
                py: 1.5,
                borderRadius: '10px',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                fontFamily: materialTheme.typography.fontFamily,
                background: BRAND_GRADIENT,
                color: '#ffffff',
                boxShadow: '0 6px 18px rgba(43, 34, 88, 0.22)',
                transition: 'transform 180ms ease, box-shadow 220ms ease, filter 220ms ease',
                position: 'relative',
                overflow: 'hidden',
                /* Gold underline that grows on hover */
                '&::after': {
                  content: '""',
                  position: 'absolute',
                  left: '50%',
                  bottom: 8,
                  width: '30%',
                  height: 2,
                  backgroundColor: GOLD,
                  transform: 'translateX(-50%) scaleX(0)',
                  transformOrigin: 'center',
                  transition: 'transform 260ms cubic-bezier(0.4, 0, 0.2, 1)',
                  borderRadius: 2
                },
                '&:hover': {
                  background: BRAND_GRADIENT,
                  filter: 'brightness(1.05)',
                  boxShadow: '0 8px 22px rgba(43, 34, 88, 0.28)'
                },
                '&:hover::after': {
                  transform: 'translateX(-50%) scaleX(1)'
                },
                '&:disabled': {
                  background: '#C7C9D3',
                  color: '#ffffff',
                  boxShadow: 'none'
                }
              }}
              disabled={loading}
            >
              {loading ? 'Signing In...' : 'Sign In'}
            </Button>

            <Box
              sx={{
                mt: 4,
                pt: 3,
                borderTop: '1px solid rgba(43, 34, 88, 0.08)',
                textAlign: 'center'
              }}
            >
              <Typography
                variant="caption"
                sx={{
                  color: materialTheme.palette.text.disabled,
                  fontSize: '0.8rem',
                  letterSpacing: '0.4px',
                  fontFamily: materialTheme.typography.fontFamily
                }}
              >
                Secure enterprise authentication
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {/* Forgot Password Dialog */}
      <Dialog
        open={forgotPasswordOpen}
        onClose={handleCloseForgotPassword}
        maxWidth="sm"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            fontFamily: materialTheme.typography.fontFamily
          }
        }}
      >
        <DialogTitle
          sx={{
            fontWeight: 700,
            color: materialTheme.palette.text.primary,
            fontFamily: materialTheme.typography.fontFamily,
            pb: 2
          }}
        >
          Reset Password
        </DialogTitle>
        <DialogContent>
          <Typography
            variant="body2"
            sx={{
              color: materialTheme.palette.text.secondary,
              mb: 3,
              fontFamily: materialTheme.typography.fontFamily
            }}
          >
            Enter your email address and we&apos;ll send you a link to reset your password.
          </Typography>

          {forgotPasswordSuccess && (
            <Box sx={{ mb: 2 }}>
              <Alert severity={'success'} title={'Success'} message={forgotPasswordSuccess} />
            </Box>
          )}

          {forgotPasswordError && (
            <Box sx={{ mb: 2 }}>
              <Alert severity={'error'} title={'Error'} message={forgotPasswordError} />
            </Box>
          )}

          <TextField
            name="forgotPasswordEmail"
            label="Email Address"
            fullWidth
            required
            type="email"
            onChange={(e) => setForgotPasswordEmail(e.target.value)}
            placeholder={'name@company.com'}
            errorMessage={forgotPasswordError && !forgotPasswordSuccess ? forgotPasswordError : ''}
            value={forgotPasswordEmail}
            disabled={!!forgotPasswordSuccess}
            sx={inputSx}
          />
        </DialogContent>
        <DialogActions
          sx={{
            px: 3,
            pb: 3,
            pt: 2
          }}
        >
          <Button
            onClick={handleCloseForgotPassword}
            variant="outlined"
            disabled={forgotPasswordLoading}
            sx={{
              textTransform: 'none',
              fontFamily: materialTheme.typography.fontFamily,
              borderColor: 'rgba(43, 34, 88, 0.25)',
              color: materialTheme.palette.text.secondary,
              borderRadius: '10px',
              '&:hover': {
                borderColor: '#2b2258',
                backgroundColor: 'rgba(43, 34, 88, 0.04)'
              }
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleForgotPassword}
            variant="contained"
            disabled={forgotPasswordLoading || !!forgotPasswordSuccess}
            sx={{
              textTransform: 'none',
              fontFamily: materialTheme.typography.fontFamily,
              background: BRAND_GRADIENT,
              color: '#ffffff',
              borderRadius: '10px',
              boxShadow: '0 4px 12px rgba(43, 34, 88, 0.18)',
              '&:hover': {
                background: BRAND_GRADIENT,
                filter: 'brightness(1.05)',
                boxShadow: '0 6px 16px rgba(43, 34, 88, 0.24)'
              },
              '&:disabled': {
                background: '#C7C9D3',
                color: '#ffffff'
              }
            }}
          >
            {forgotPasswordLoading ? 'Sending...' : 'Send Reset Link'}
          </Button>
        </DialogActions>
      </Dialog>
    </StyledLogin>
  );
};

export default Login;
